"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useEffect, useRef, useState } from "react";
import RainforestSkeleton from "@/components/rainforest/rainforest-skeleton";
import { useRainforestTheme } from "@/components/rainforest/use-rainforest-theme";
import { Button } from "@/components/ui";
import { loadPaymentSdk } from "@/lib/payments/load-payment-sdk";
import { registerPaymentSurface } from "@/lib/payments/payment-surface-registry";
import { cx } from "@/lib/tailwind-utils";
import { RainforestPaymentMethodConfig } from "@/types/rainforest";

/**
 * The embedded form only reports itself ready once its config call and all of
 * its cross-origin field frames have completed their handshakes. Any one of
 * those failing silently leaves the form loading forever, so give up waiting
 * and offer a retry instead of showing the skeleton indefinitely.
 */
const LOAD_TIMEOUT_MS = 10_000;
interface RainforestPaymentMethodProps {
  paymentMethodConfig: RainforestPaymentMethodConfig;
  handleApproved: (event: any) => void;
  handleDeclined: (event: any) => void;
  handleError: (event: any) => void;
  handleValid: (event: any) => void;
  handleInvalid: (event: any) => void;
  handleMethodUpdated?: (event: any) => void;
  className?: string;
  paymentMethodConfigId?: string;
  hideButton?: boolean;
  fontSize?: string;
  allowedMethods?: string;
  /** Style overrides for the Rainforest web component (maps to style-* attributes) */
  styleOverrides?: {
    buttonColor?: string;
    borderColor?: string;
    borderRadius?: string;
    borderWidth?: string;
    iconColor?: string;
    errorBorderColor?: string;
    errorMessageColor?: string;
  };
}
const RainforestPaymentMethod: React.FC<RainforestPaymentMethodProps> = t0 => {
  const $ = _c(69);
  const {
    paymentMethodConfig,
    handleApproved,
    handleDeclined,
    handleError,
    handleValid,
    handleInvalid,
    handleMethodUpdated,
    className,
    hideButton: t1,
    fontSize: t2,
    allowedMethods: t3,
    styleOverrides
  } = t0;
  const hideButton = t1 === undefined ? true : t1;
  const fontSize = t2 === undefined ? "14px" : t2;
  const allowedMethods = t3 === undefined ? "CARD,ACH,APPLE_PAY" : t3;
  const componentRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [didNotLoad, setDidNotLoad] = useState(false);
  const [hasEverLoaded, setHasEverLoaded] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const rainforestTheme = useRainforestTheme();
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setIsLoading(false);
      setDidNotLoad(false);
      setHasEverLoaded(true);
    };
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const handleLoaded = t4;
  let t5;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      console.log("Payment method form is loading");
      setIsLoading(true);
    };
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const handleLoading = t5;
  let t6;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setDidNotLoad(false);
      setIsLoading(true);
      setAttempt(_temp);
    };
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  const handleRetry = t6;
  let t7;
  if ($[3] !== paymentMethodConfig.sandbox) {
    t7 = () => {
      let cancelled = false;
      loadPaymentSdk("RAINFOREST", paymentMethodConfig.sandbox).catch(error => {
        console.error("Failed to load the Rainforest payment SDK", error);
        if (!cancelled) {
          setDidNotLoad(true);
        }
      });
      return () => {
        cancelled = true;
      };
    };
    $[3] = paymentMethodConfig.sandbox;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  let t8;
  if ($[5] !== attempt || $[6] !== paymentMethodConfig.sandbox) {
    t8 = [paymentMethodConfig.sandbox, attempt];
    $[5] = attempt;
    $[6] = paymentMethodConfig.sandbox;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  useEffect(t7, t8);
  let t9;
  if ($[8] !== hasEverLoaded || $[9] !== isLoading) {
    t9 = () => {
      if (!isLoading || hasEverLoaded) {
        return;
      }
      const timer = setTimeout(() => setDidNotLoad(true), LOAD_TIMEOUT_MS);
      return () => clearTimeout(timer);
    };
    $[8] = hasEverLoaded;
    $[9] = isLoading;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== attempt || $[12] !== hasEverLoaded || $[13] !== isLoading) {
    t10 = [isLoading, hasEverLoaded, attempt];
    $[11] = attempt;
    $[12] = hasEverLoaded;
    $[13] = isLoading;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  useEffect(t9, t10);
  let t11;
  if ($[15] !== handleApproved) {
    t11 = event => {
      setIsLoading(false);
      handleApproved(event);
    };
    $[15] = handleApproved;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const wrappedHandleApproved = t11;
  let t12;
  if ($[17] !== handleDeclined) {
    t12 = event_0 => {
      setIsLoading(false);
      handleDeclined(event_0);
    };
    $[17] = handleDeclined;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  const wrappedHandleDeclined = t12;
  let t13;
  if ($[19] !== handleError) {
    t13 = event_1 => {
      setIsLoading(false);
      handleError(event_1);
    };
    $[19] = handleError;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  const wrappedHandleError = t13;
  let t14;
  let t15;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => registerPaymentSurface({
      submit: () => componentRef.current?.querySelector("rainforest-payment")?.submit()
    });
    t15 = [];
    $[21] = t14;
    $[22] = t15;
  } else {
    t14 = $[21];
    t15 = $[22];
  }
  useEffect(t14, t15);
  let t16;
  if ($[23] !== handleInvalid || $[24] !== handleMethodUpdated || $[25] !== handleValid || $[26] !== wrappedHandleApproved || $[27] !== wrappedHandleDeclined || $[28] !== wrappedHandleError) {
    t16 = () => {
      const component = componentRef.current ? componentRef.current.querySelector("rainforest-payment") : null;
      if (component) {
        component.addEventListener("approved", wrappedHandleApproved);
        component.addEventListener("declined", wrappedHandleDeclined);
        component.addEventListener("error", wrappedHandleError);
        component.addEventListener("valid", handleValid);
        component.addEventListener("invalid", handleInvalid);
        component.addEventListener("loaded", handleLoaded);
        component.addEventListener("loading", handleLoading);
        if (handleMethodUpdated) {
          component.addEventListener("method-updated", handleMethodUpdated);
        }
        return () => {
          component.removeEventListener("approved", wrappedHandleApproved);
          component.removeEventListener("declined", wrappedHandleDeclined);
          component.removeEventListener("error", wrappedHandleError);
          component.removeEventListener("valid", handleValid);
          component.removeEventListener("invalid", handleInvalid);
          component.removeEventListener("loaded", handleLoaded);
          component.removeEventListener("loading", handleLoading);
          if (handleMethodUpdated) {
            component.removeEventListener("method-updated", handleMethodUpdated);
          }
        };
      }
    };
    $[23] = handleInvalid;
    $[24] = handleMethodUpdated;
    $[25] = handleValid;
    $[26] = wrappedHandleApproved;
    $[27] = wrappedHandleDeclined;
    $[28] = wrappedHandleError;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== attempt || $[31] !== handleInvalid || $[32] !== handleMethodUpdated || $[33] !== handleValid || $[34] !== wrappedHandleApproved || $[35] !== wrappedHandleDeclined || $[36] !== wrappedHandleError) {
    t17 = [wrappedHandleApproved, wrappedHandleDeclined, wrappedHandleError, handleValid, handleInvalid, handleLoaded, handleLoading, handleMethodUpdated, attempt];
    $[30] = attempt;
    $[31] = handleInvalid;
    $[32] = handleMethodUpdated;
    $[33] = handleValid;
    $[34] = wrappedHandleApproved;
    $[35] = wrappedHandleDeclined;
    $[36] = wrappedHandleError;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  useEffect(t16, t17);
  let t18;
  if ($[38] !== className) {
    t18 = cx("rounded-lg border border-border p-4 pb-0", className);
    $[38] = className;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  const t19 = hideButton ? hideButton : undefined;
  const t20 = isLoading ? "none" : "block";
  let t21;
  if ($[40] !== t20) {
    t21 = {
      display: t20
    };
    $[40] = t20;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  const t22 = styleOverrides?.buttonColor ?? rainforestTheme["style-button-color"];
  const t23 = styleOverrides?.borderColor ?? rainforestTheme["style-border-color"];
  const t24 = styleOverrides?.borderRadius;
  const t25 = styleOverrides?.borderWidth;
  const t26 = styleOverrides?.iconColor ?? rainforestTheme["style-icon-color"];
  const t27 = styleOverrides?.errorBorderColor;
  const t28 = styleOverrides?.errorMessageColor;
  let t29;
  if ($[42] !== allowedMethods || $[43] !== attempt || $[44] !== fontSize || $[45] !== paymentMethodConfig.paymentMethodConfigId || $[46] !== paymentMethodConfig.sessionKey || $[47] !== rainforestTheme || $[48] !== t19 || $[49] !== t21 || $[50] !== t22 || $[51] !== t23 || $[52] !== t24 || $[53] !== t25 || $[54] !== t26 || $[55] !== t27 || $[56] !== t28) {
    t29 = <rainforest-payment key={attempt} session-key={paymentMethodConfig.sessionKey} payment-method-config-id={paymentMethodConfig.paymentMethodConfigId} allowed-methods={allowedMethods} style-font-family="-apple-system, system-ui, Inter" hide-button={t19} style={t21} style-font-size={fontSize} style-container-padding="0px" {...rainforestTheme} style-button-color={t22} style-border-color={t23} style-border-radius={t24} style-border-width={t25} style-icon-color={t26} style-error-border-color={t27} style-error-message-color={t28} />;
    $[42] = allowedMethods;
    $[43] = attempt;
    $[44] = fontSize;
    $[45] = paymentMethodConfig.paymentMethodConfigId;
    $[46] = paymentMethodConfig.sessionKey;
    $[47] = rainforestTheme;
    $[48] = t19;
    $[49] = t21;
    $[50] = t22;
    $[51] = t23;
    $[52] = t24;
    $[53] = t25;
    $[54] = t26;
    $[55] = t27;
    $[56] = t28;
    $[57] = t29;
  } else {
    t29 = $[57];
  }
  let t30;
  if ($[58] !== didNotLoad || $[59] !== isLoading) {
    t30 = isLoading && !didNotLoad && <RainforestSkeleton padding="sm" backgroundColor="bg-transparent" className="-m-4 mb-0" />;
    $[58] = didNotLoad;
    $[59] = isLoading;
    $[60] = t30;
  } else {
    t30 = $[60];
  }
  let t31;
  if ($[61] !== didNotLoad || $[62] !== isLoading) {
    t31 = isLoading && didNotLoad && <div className="pb-4"><p className="mb-3 text-body text-muted-foreground">The payment form didn't finish loading.</p><Button variant="secondary" onClick={handleRetry}>Try again</Button></div>;
    $[61] = didNotLoad;
    $[62] = isLoading;
    $[63] = t31;
  } else {
    t31 = $[63];
  }
  let t32;
  if ($[64] !== t18 || $[65] !== t29 || $[66] !== t30 || $[67] !== t31) {
    t32 = <div ref={componentRef} className={t18}>{t29}{t30}{t31}</div>;
    $[64] = t18;
    $[65] = t29;
    $[66] = t30;
    $[67] = t31;
    $[68] = t32;
  } else {
    t32 = $[68];
  }
  return t32;
};
export default RainforestPaymentMethod;
function _temp(previous) {
  return previous + 1;
}